import clsx from 'clsx';
import React, {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { CenterContainer } from '@/components/CenterContainer/CenterContainer';
import Result from '@/components/Result/Result';
import Title from '@/components/Title/Title';

import classes from './classes.module.css';
import Options from './Options/Options';

export const calculationMode = {
  multiplication: 'multiplication',
  addition: 'addition',
} as const;

export type Mode = (typeof calculationMode)[keyof typeof calculationMode];

export const operator = {
  multiplication: 'x',
  addition: '+',
} as const;

export type Operator = (typeof operator)[keyof typeof operator];

type Error = {
  left: number;
  right: number;
  operator: string;
};

type Props = {
  mode: Mode;
};

export const Calculation: FC<Props> = ({ mode: currentMode }) => {
  const [tables, setTables] = useState([2, 3, 5]);
  const [left, setLeft] = useState(0);
  const [right, setRight] = useState(0);
  const [expectedResult, setExpectedResult] = useState(0);
  const [selectedResult, setSelectedResult] = useState<number[]>([]);
  const [calculationCount, setCalculationCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [errors, setErrors] = useState<Error[]>([]);
  const [askOnErrors, setAskOnErrors] = useState(false);
  const isInitialized = useRef(false);

  const newCalculation = useCallback(
    (
      avoidCurrent = false,
      currentLeft?: number,
      currentRight?: number,
      currentExpectedResult?: number
    ) => {
      let newLeft, newRight, newResult;
      let attempts = 0;
      const maxAttempts = 50; // Protection contre une boucle infinie

      do {
        let firstOperand, secondOperand;
        if (askOnErrors) {
          const errorIdx = Math.floor(Math.random() * errors.length);
          firstOperand = errors[errorIdx].left;
          secondOperand = errors[errorIdx].right;
        } else {
          const leftIdx = Math.floor(Math.random() * tables.length);
          const rightValue = Math.floor(Math.random() * 11);

          firstOperand = tables[leftIdx];
          secondOperand = rightValue;
        }
        const result =
          currentMode === calculationMode.multiplication
            ? firstOperand * secondOperand
            : firstOperand + secondOperand;

        const order = Math.random() > 0.5;
        if (order) {
          newLeft = firstOperand;
          newRight = secondOperand;
        } else {
          newLeft = secondOperand;
          newRight = firstOperand;
        }
        newResult = result;
        attempts++;
      } while (
        avoidCurrent &&
        attempts < maxAttempts &&
        newLeft === (currentLeft ?? left) &&
        newRight === (currentRight ?? right) &&
        newResult === (currentExpectedResult ?? expectedResult)
      );

      setLeft(newLeft);
      setRight(newRight);
      setExpectedResult(newResult);
      setSelectedResult([]);
    },
    [left, right, expectedResult, askOnErrors, currentMode, errors, tables]
  );

  useEffect(() => {
    if (!isInitialized.current) {
      newCalculation();
      isInitialized.current = true;
    }
  }, [newCalculation]);

  const handleResultClick = (value: number) => {
    setSelectedResult((prev) => [...prev, value]);
    if (value === expectedResult) {
      setCalculationCount((prev) => prev + 1);
      setTimeout(() => {
        setSelectedResult([]);
        newCalculation(true, left, right, expectedResult); // Éviter le calcul actuel
      }, 800);
    } else {
      // Incrémenter le compteur d'erreurs seulement au premier clic incorrect
      if (!selectedResult.includes(value)) {
        setErrorCount((prev) => prev + 1);
        if (
          !errors.some(
            (error) =>
              `${error.left}${error.operator}${error.right}` ===
                `${left}${operator}${right}` ||
              `${error.right}${error.operator}${error.left}` ===
                `${left}${operator}${right}`
          )
        ) {
          setErrors((prev) => [...prev, { left, right, operator }]);
        }
      }
    }
  };

  const handleSelectTable = (value: number) => {
    setTables((prev) => {
      if (prev.includes(value)) {
        if (prev.length === 1) {
          return prev;
        }
        return prev.filter((v) => v !== value);
      }
      return [...prev, value];
    });
  };

  const maxResult = useMemo(
    () =>
      currentMode === calculationMode.multiplication
        ? Math.max(...tables) * 10
        : Math.max(...tables) + 10,
    [currentMode, tables]
  );

  const minResult = 0;

  const operator = useMemo(
    () => (currentMode === calculationMode.multiplication ? 'x' : '+'),
    [currentMode]
  );

  return (
    <CenterContainer className={classes.container}>
      <div className={classes.configSection}>
        <Title>Quelles tables vas-tu réviser ? </Title>
        <Options selectedTables={tables} onSelectTable={handleSelectTable} />
      </div>
      <div className={classes.gameSection}>
        <div className={classes.operation}>
          <span className={classes.operand}>{left}</span>
          <span className={classes.operator}>{operator}</span>
          <span className={classes.operand}>{right}</span>
        </div>
        <div>
          <Title>Je sais, ça fait : </Title>
          <div className={classes.possibleResults}>
            {Array.from(
              { length: maxResult - minResult + 1 },
              (_, i) => i + minResult
            ).map((value) => (
              <Result
                key={value}
                value={value}
                onClick={() => handleResultClick(value)}
                isBad={
                  selectedResult.includes(value) && value !== expectedResult
                }
                isCorrect={
                  selectedResult.includes(value) && value === expectedResult
                }
              />
            ))}
          </div>
        </div>
      </div>
      <div className={classes.counterContainer}>
        <div className={classes.countersGroup}>
          <span className={classes.counter}>
            Calculs réussis :{' '}
            <span className={classes.successCount}>{calculationCount}</span>
          </span>
          <span className={classes.counter}>
            Erreurs : <span className={classes.errorCount}>{errorCount}</span>
          </span>
        </div>
        <button
          className={classes.resetButton}
          onClick={() => {
            setCalculationCount(0);
            setErrorCount(0);
            setErrors([]);
            setAskOnErrors(false);
          }}
          aria-label="Réinitialiser les compteurs"
        >
          Reset
        </button>
        <button
          className={clsx(
            classes.resetButton,
            askOnErrors && classes.buttonActive
          )}
          onClick={() => {
            setAskOnErrors(!askOnErrors);
          }}
          aria-label="Interroger sur les erreurs"
        >
          Réviser les erreurs
        </button>
      </div>
      <div className={classes.errors}>
        {errors.map((error, index) => (
          <Fragment key={error.left + error.right + error.operator}>
            <div>
              {error.left} {error.operator} {error.right}
            </div>
            {index !== errors.length - 1 && <span> / </span>}
          </Fragment>
        ))}
      </div>
    </CenterContainer>
  );
};
